import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, ShoppingCart } from 'lucide-react';
import { Post } from '../types';
import { adsDisplayService } from '../services/adsDisplayService';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

interface AdCardProps {
  post: Post;
}

const AdCard: React.FC<AdCardProps> = ({ post }) => {
  const { user } = useAuth();
  const { select } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showAudioControl, setShowAudioControl] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const hasTrackedImpressionRef = useRef(false);
  const impressionTimerRef = useRef<number | null>(null);
  const exposureTimerRef = useRef<number | null>(null);
  const exposureInViewRef = useRef(false);
  const exposureLastRatioRef = useRef(0);

  const ctaLabels: Record<string, { es: string; en: string }> = {
    learn_more: { es: 'Más información', en: 'Learn more' },
    shop_now: { es: 'Comprar ahora', en: 'Shop now' },
    sign_up: { es: 'Registrarse', en: 'Sign up' },
    download: { es: 'Descargar', en: 'Download' },
    contact_us: { es: 'Contactar', en: 'Contact us' },
    book_now: { es: 'Reservar ahora', en: 'Book now' },
  };

  const getCallToActionText = () => select(ctaLabels[post.call_to_action || ''] || { es: 'Ver más', en: 'View more' });

  useEffect(() => {
    const adId = post.type === 'ad' ? post.ad_id : undefined;
    if (!adId) return;
    if (hasTrackedImpressionRef.current) return;

    const storageKey = `soartistic:ad_impression:${adId}`;
    try {
      if (sessionStorage.getItem(storageKey)) {
        hasTrackedImpressionRef.current = true;
        return;
      }
    } catch {
      // ignore
    }

    const target = rootRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const ratio = entry?.intersectionRatio ?? 0;

        if (hasTrackedImpressionRef.current) return;

        if (ratio >= 0.5) {
          if (impressionTimerRef.current) return;
          impressionTimerRef.current = window.setTimeout(() => {
            if (hasTrackedImpressionRef.current) return;
            hasTrackedImpressionRef.current = true;
            try {
              sessionStorage.setItem(storageKey, '1');
            } catch {
              // ignore
            }
            void adsDisplayService.trackAdImpression(adId, user?.id);
          }, 900);
          return;
        }

        if (impressionTimerRef.current) {
          window.clearTimeout(impressionTimerRef.current);
          impressionTimerRef.current = null;
        }
      },
      { threshold: [0, 0.5, 1] }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
      if (impressionTimerRef.current) {
        window.clearTimeout(impressionTimerRef.current);
        impressionTimerRef.current = null;
      }
    };
  }, [post.ad_id, post.type, user?.id]);

  useEffect(() => {
    const adId = post.type === 'ad' ? post.ad_id : undefined;
    if (!adId) return;

    const target = rootRef.current;
    if (!target) return;

    const viewerKey = user?.id ?? adsDisplayService.getSessionId();
    const blockMs = adsDisplayService.getAdTimeBlockMs();

    const stop = () => {
      exposureInViewRef.current = false;
      if (exposureTimerRef.current) {
        window.clearTimeout(exposureTimerRef.current);
        exposureTimerRef.current = null;
      }
    };

    const scheduleNext = () => {
      if (exposureTimerRef.current) return;
      exposureTimerRef.current = window.setTimeout(async () => {
        exposureTimerRef.current = null;
        if (!exposureInViewRef.current) return;
        if (document.hidden) return;
        await adsDisplayService.trackAdTimeBlock({ adId, viewerKey, placement: 'feed' });
        scheduleNext();
      }, blockMs);
    };

    const maybeStart = () => {
      if (exposureInViewRef.current) return;
      exposureInViewRef.current = true;
      scheduleNext();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const ratio = entry?.intersectionRatio ?? 0;
        exposureLastRatioRef.current = ratio;

        if (document.hidden) {
          stop();
          return;
        }

        if (ratio >= 0.5) {
          maybeStart();
          return;
        }

        stop();
      },
      { threshold: [0, 0.5, 1] }
    );

    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
        return;
      }
      if (exposureLastRatioRef.current >= 0.5) {
        maybeStart();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    observer.observe(target);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      observer.disconnect();
      stop();
    };
  }, [post.ad_id, post.type, user?.id]);

  const handleAdClick = (shouldOpenUrl: boolean) => {
    if (post.type === 'ad' && post.ad_id) {
      console.log('🖱️ Ad CTA clicked:', post.ad_id, post.title);
      // Track ad click
      void adsDisplayService.trackAdClick(post.ad_id, user?.id);
      
      // Open destination URL if available
      if (shouldOpenUrl && post.destination_url) {
        console.log('🌐 Opening destination URL:', post.destination_url);
        window.open(post.destination_url, '_blank', 'noopener,noreferrer');
      } else {
        console.log('ℹ️ No destination URL for ad');
      }
    }
  };

  const handleVideoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      // Auto-unmute when playing for better ad experience
      video.muted = false;
      setIsMuted(false);
      video.play();
      setIsPlaying(true);
    }
  };

  const handleVideoToggleSound = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const renderMedia = () => {
    if (!post.media || post.media.length === 0) {
      return (
        <div className="w-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center" style={{ aspectRatio: '16/9' }}>
          <div className="text-white text-center">
            <div className="text-4xl mb-2">🎨</div>
            <p className="font-medium">{select({ es: 'Arte promocionado', en: 'Promoted art' })}</p>
          </div>
        </div>
      );
    }

    const media = post.media[0];

    if (media.type === 'image') {
      return (
        <img
          src={media.url}
          alt={post.title}
          className="w-full h-auto object-cover cursor-pointer"
          onClick={() => handleAdClick(true)}
          onError={(e) => {
            e.currentTarget.src = 'https://images.pexels.com/photos/1183992/pexels-photo-1183992.jpeg?auto=compress&cs=tinysrgb&w=400';
          }}
        />
      );
    }

    if (media.type === 'video') {
      return (
        <div className="relative">
          <video
            ref={videoRef}
            src={media.url}
            muted={isMuted}
            className="w-full h-auto object-cover"
            style={{ aspectRatio: '16/9' }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onMouseEnter={() => setShowAudioControl(true)}
            onMouseLeave={() => setShowAudioControl(false)}
            onError={() => {
              console.error('Error loading video:', media.url);
            }}
          />
          <div 
            className="absolute inset-0 flex items-center justify-center cursor-pointer"
            onClick={handleVideoClick}
          >
            <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all">
              {isPlaying ? (
                <Pause size={24} className="text-gray-800" />
              ) : (
                <Play size={24} className="text-gray-800 ml-1" />
              )}
            </div>
          </div>
          
          {/* Audio Control - Always visible on mobile, hover on desktop */}
          <div className={`absolute top-3 right-3 transition-all duration-300 ${
            showAudioControl || window.innerWidth <= 768 ? 'opacity-100' : 'opacity-0'
          }`}>
            <button
              onClick={handleVideoToggleSound}
              className="w-10 h-10 bg-black bg-opacity-80 backdrop-blur-sm text-white rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all shadow-lg border-2 border-white border-opacity-30"
              title={isMuted ? select({ es: 'Activar audio', en: 'Enable audio' }) : select({ es: 'Silenciar audio', en: 'Mute audio' })}
            >
              {isMuted ? (
                <div className="relative">
                  <span className="text-lg">🔇</span>
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                </div>
              ) : (
                <span className="text-lg">🔊</span>
              )}
            </button>
          </div>
          
          {/* Muted Indicator */}
          {isMuted && isPlaying && (
            <div className="absolute bottom-3 left-3 bg-gray-600 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
              <span>🔇</span>
              {select({ es: 'Silenciado', en: 'Muted' })}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="w-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center" style={{ aspectRatio: '16/9' }}>
        <div className="text-white text-center">
          <div className="text-4xl mb-2">📄</div>
          <p className="font-medium">{select({ es: 'Contenido promocionado', en: 'Promoted content' })}</p>
        </div>
      </div>
    );
  };

  return (
    <div 
      ref={rootRef}
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-300 self-start"
      style={{ width: '100%', height: 'fit-content' }}
    >
      {/* Sponsored Badge */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3">
        <div className="flex items-center justify-center">
          <span className="text-xs font-bold flex items-center gap-2">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            {select({ es: 'PUBLICIDAD', en: 'AD' })}
          </span>
        </div>
      </div>

      {/* Media */}
      <div className="relative">
        {renderMedia()}
      </div>

      {/* Content */}
      <div className="p-4">
        <h2 className="text-lg font-bold text-gray-900 mb-2">{post.title}</h2>
        <p className="text-gray-700 mb-4 line-clamp-3">{post.description}</p>
        
        {/* CTA Button */}
        <button
          onClick={() => handleAdClick(true)}
          className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all transform hover:scale-[1.02] shadow-sm hover:shadow-md font-bold flex items-center justify-center gap-2"
        >
          {post.call_to_action === 'shop_now' && <ShoppingCart size={16} />}
          {getCallToActionText()}
        </button>
      </div>
    </div>
  );
};

export default AdCard;
